/**
 * InlineCTA — Contextual call-to-action components for content pages.
 * Multiple variants for different contexts within articles.
 */
import { useState } from "react";
import { Lightning, EnvelopeSimple, Check, ArrowRight, Trophy, ChartBar, Crosshair, TwitterLogo, RedditLogo, FacebookLogo, LinkedinLogo } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

export function ToolCTA({ text, subtext, buttonText, buttonLink, icon }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-4 my-6 flex flex-col sm:flex-row items-center gap-4 rounded-sm">
      <div className="flex-1 text-center sm:text-left">
        <p className="text-sm text-white font-semibold">{text}</p>
        {subtext && <p className="text-[10px] text-[#888] mt-0.5">{subtext}</p>}
      </div>
      <Link to={buttonLink || "/trade-calculator"} className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors shrink-0">
        {icon || <Lightning size={12} weight="fill" />} {buttonText || "TRY IT FREE"}
      </Link>
    </div>
  );
}

export function InlineNewsletter({ context = "weekly", source = "inline" }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const messages = {
    weekly: { head: "Get the weekly dynasty update", sub: "Value movers, trade targets, and data drops — free, every week." },
    rookie: { head: "Get rookie value alerts", sub: "Be first to know when rookie values spike or crash." },
    deadline: { head: "Never miss a trade window", sub: "Deadline analysis and buy/sell targets delivered to your inbox." },
    rebuild: { head: "Get the rebuild playbook", sub: "Step-by-step rebuild strategy with real data — free in your inbox." },
    championship: { head: "Get championship insights", sub: "Data-driven findings from 6,976 dynasty champions, weekly." },
  };
  const msg = messages[context] || messages.weekly;

  if (done) return (
    <div className="border border-[#4ade80]/30 bg-[#4ade80]/5 p-4 my-6 flex items-center gap-2 rounded-sm">
      <Check size={16} weight="bold" className="text-[#4ade80]" />
      <p className="text-sm text-[#4ade80]">You are in. Watch your inbox.</p>
    </div>
  );

  return (
    <div className="border border-[#262626] bg-[#171717] p-4 my-6 rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <EnvelopeSimple size={14} weight="bold" className="text-[#ccff00]" />
        <p className="text-xs font-bold text-white uppercase">{msg.head}</p>
      </div>
      <p className="text-[10px] text-[#888] mb-3">{msg.sub}</p>
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (!email.includes("@")) return;
        try { await axios.post(`${API}/newsletter/subscribe`, { email, source: `${source}_${context}` }); } catch {}
        setDone(true);
      }} className="flex gap-2">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required className="flex-1 h-9 px-3 bg-[#0a0a0a] border border-[#333] text-white text-xs rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none" />
        <button type="submit" className="h-9 px-4 bg-[#ccff00] text-black text-[10px] font-bold rounded-sm hover:bg-[#b8e600] transition-colors shrink-0">SUBSCRIBE</button>
      </form>
    </div>
  );
}

export function ScoreCTA({ text }) {
  return (
    <div className="border border-[#ccff00]/20 bg-[#ccff00]/5 p-4 my-6 text-center rounded-sm">
      <p className="text-sm text-white font-semibold mb-2">{text || "How does your roster compare?"}</p>
      <p className="text-[10px] text-[#888] mb-3">Import your Sleeper league to get your Championship Score and contender ranking.</p>
      <button onClick={() => { window.location.href = "/api/auth/google/login"; }} className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 font-bold hover:bg-[#b8e600] transition-colors">
        <Trophy size={12} weight="fill" /> SCORE MY ROSTER — FREE
      </button>
    </div>
  );
}


export function ShareBar({ title, path }) {
  const url = `https://dynastytrademaker.com${path}`;
  const text = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);

  return (
    <div className="flex items-center gap-3" data-testid="share-bar">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#555]">SHARE</span>
      <a href={`https://twitter.com/intent/tweet?text=${text}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#1DA1F2] hover:text-[#1DA1F2] transition-colors no-underline" title="Share on X" data-testid="share-twitter">
        <TwitterLogo size={14} weight="bold" />
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#4267B2] hover:text-[#4267B2] transition-colors no-underline" title="Share on Facebook" data-testid="share-facebook">
        <FacebookLogo size={14} weight="bold" />
      </a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#0A66C2] hover:text-[#0A66C2] transition-colors no-underline" title="Share on LinkedIn" data-testid="share-linkedin">
        <LinkedinLogo size={14} weight="bold" />
      </a>
      <a href={`https://www.reddit.com/submit?url=${encodedUrl}&title=${text}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-8 h-8 border border-[#333] rounded-sm text-[#888] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors no-underline" title="Share on Reddit" data-testid="share-reddit">
        <RedditLogo size={14} weight="bold" />
      </a>
    </div>
  );
}


const RELATED_MAP = {
  // Trading cluster
  "do-teams-that-trade-more-win": ["what-winning-trades-look-like", "who-wins-lopsided-trades", "when-to-make-your-move"],
  "what-winning-trades-look-like": ["do-teams-that-trade-more-win", "who-wins-lopsided-trades", "when-to-make-your-move"],
  "who-wins-lopsided-trades": ["what-winning-trades-look-like", "do-teams-that-trade-more-win", "how-lopsided-is-the-average-trade"],
  "when-to-make-your-move": ["do-teams-that-trade-more-win", "what-winning-trades-look-like", "who-wins-lopsided-trades"],
  "how-lopsided-is-the-average-trade": ["who-wins-lopsided-trades", "what-winning-trades-look-like", "do-teams-that-trade-more-win"],
  // Roster cluster
  "what-championship-rosters-have-in-common": ["stars-vs-depth", "where-champions-put-their-value", "championship-dna-players"],
  "stars-vs-depth": ["how-many-studs-to-win", "what-championship-rosters-have-in-common", "where-champions-put-their-value"],
  "where-champions-put-their-value": ["do-you-need-an-elite-qb", "do-champions-pay-up-at-tight-end", "stars-vs-depth"],
  "how-many-studs-to-win": ["stars-vs-depth", "what-championship-rosters-have-in-common", "championship-dna-players"],
  "championship-dna-players": ["what-championship-rosters-have-in-common", "how-many-studs-to-win", "where-champions-put-their-value"],
  "do-you-need-an-elite-qb": ["where-champions-put-their-value", "superflex-qb-longevity", "what-championship-rosters-have-in-common"],
  "do-champions-pay-up-at-tight-end": ["where-champions-put-their-value", "what-championship-rosters-have-in-common", "stars-vs-depth"],
  "the-real-age-of-a-champion": ["when-players-lose-value-by-age", "what-championship-rosters-have-in-common", "when-running-backs-fall-off"],
  "how-champions-build-rosters": ["draft-picks-that-win-championships", "what-championship-rosters-have-in-common", "stars-vs-depth"],
  // Draft cluster
  "startup-strategies-that-win": ["what-happens-in-startup-drafts", "which-rookie-picks-hit", "draft-picks-that-win-championships"],
  "what-happens-in-startup-drafts": ["startup-strategies-that-win", "which-rookie-picks-hit", "where-champions-put-their-value"],
  "which-rookie-picks-hit": ["draft-picks-that-win-championships", "startup-strategies-that-win", "how-champions-build-rosters"],
  "draft-picks-that-win-championships": ["which-rookie-picks-hit", "how-champions-build-rosters", "startup-strategies-that-win"],
  // Rebuild cluster
  "how-long-a-rebuild-takes": ["fastest-way-out-of-a-rebuild", "do-dynasties-repeat", "how-champions-build-rosters"],
  "fastest-way-out-of-a-rebuild": ["how-long-a-rebuild-takes", "do-dynasties-repeat", "when-to-make-your-move"],
  "do-dynasties-repeat": ["how-long-a-rebuild-takes", "fastest-way-out-of-a-rebuild", "what-championship-rosters-have-in-common"],
  // Aging cluster
  "when-players-lose-value-by-age": ["when-running-backs-fall-off", "why-elite-wrs-are-safest", "superflex-qb-longevity"],
  "how-long-dynasty-assets-hold-value": ["when-players-lose-value-by-age", "when-running-backs-fall-off", "why-elite-wrs-are-safest"],
  "when-running-backs-fall-off": ["why-elite-wrs-are-safest", "when-players-lose-value-by-age", "the-real-age-of-a-champion"],
  "why-elite-wrs-are-safest": ["when-running-backs-fall-off", "where-champions-put-their-value", "when-players-lose-value-by-age"],
  "superflex-qb-longevity": ["do-you-need-an-elite-qb", "when-players-lose-value-by-age", "how-long-dynasty-assets-hold-value"],
};

const TITLE_MAP = {
  "do-teams-that-trade-more-win": "Do Teams That Trade More Win?",
  "what-winning-trades-look-like": "What a Winning Trade Looks Like",
  "who-wins-lopsided-trades": "Who Wins Lopsided Trades?",
  "when-to-make-your-move": "When to Make Your Move",
  "how-lopsided-is-the-average-trade": "The Average Trade Is 40% Lopsided",
  "what-championship-rosters-have-in-common": "Championship Rosters in Common",
  "stars-vs-depth": "Stars vs Depth",
  "where-champions-put-their-value": "Where Champions Put Their Value",
  "how-many-studs-to-win": "How Many Studs to Win?",
  "championship-dna-players": "Championship DNA Players",
  "do-you-need-an-elite-qb": "Do You Need an Elite QB?",
  "do-champions-pay-up-at-tight-end": "Do Winners Pay Up at TE?",
  "the-real-age-of-a-champion": "The Real Age of a Champion",
  "how-champions-build-rosters": "How Champions Build Rosters",
  "startup-strategies-that-win": "Startup Strategies That Win",
  "what-happens-in-startup-drafts": "What Happens in Startup Drafts",
  "which-rookie-picks-hit": "Which Rookie Picks Hit?",
  "draft-picks-that-win-championships": "Draft Picks That Win Championships",
  "how-long-a-rebuild-takes": "How Long a Rebuild Takes",
  "fastest-way-out-of-a-rebuild": "Fastest Way Out of a Rebuild",
  "do-dynasties-repeat": "Do Dynasties Repeat?",
  "when-players-lose-value-by-age": "When Each Position Falls Off",
  "how-long-dynasty-assets-hold-value": "How Long Assets Hold Value",
  "when-running-backs-fall-off": "When Running Backs Fall Off",
  "why-elite-wrs-are-safest": "Why Elite WRs Are Safest",
  "superflex-qb-longevity": "Superflex QB Longevity",
};

export function RelatedResearch({ slug }) {
  const related = RELATED_MAP[slug];
  if (!related || related.length === 0) return null;
  return (
    <div className="mt-8 border-t border-[#262626] pt-6">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">RELATED RESEARCH</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {related.map(s => (
          <Link key={s} to={`/research/${s}`} className="p-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm">
            <p className="text-xs text-white font-semibold">{TITLE_MAP[s] || s}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
